/**
 * @module app.ext.iccc
 */

import { autoinject, transient } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { ApiQuery } from 'app/shared/lib/essette-api';
import * as CM from 'app/cm/models/essette-api';

/*
    The application calls the Essette API, which in turn calls the ICCC API.
    All ICCC.* actions piggyback off of an Essette model (e.g. CM.CaseAssessment, CM.CaseAssessmentAttachment, Core.ExternalSystem).
    This is because we do not have models for the ICCC objects.
*/

@transient()
@autoinject()
export class InterQualCoordinatedCareContentService {

    ////****************** Functions ************************************************////

    constructor(
        private readonly api: Api,
        private readonly logger: AppLogger) {

        this.logger.source = this;
    }

    public async post<T>(query: ApiQuery): Promise<IApiQueryResults<T>> {
        return await this.api.post(query) as IApiQueryResults<T>;
    }

    ////****************** Queries **************************************************////

    public async loadAssessmentList(): Promise<any> {
        const action = this.api.action('Iccc.AssessmentList', 'ExternalSystems', {});
        return await this.api.post(action);
    }

    public async loadImportedAssessments(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CaseAssessment>> {
        const query = this.api
            .query('CM/Cases/Assessments')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterString('externalSystem.code', 'ICCC');

        return await this.api.get(query);
    }

    // Gets ICCC Assessment by way of CM.CaseAssessmentAttachment
    public async getAssessmentContent(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.Assessment', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    // Gets ICCC Assessment by way of CM.CaseAssessment
    public async loadAssessmentContent(assessmentId: string): Promise<any> {
        const action = this.api.action('Iccc.AssessmentContent', `CM/Cases/Assessments/${assessmentId}`, {});
        return await this.api.post(action);
    }

    public async getViewerUrl(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.AssessmentUrl', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    public async getCarePlanViewerUrl(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.CarePlanUrl', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    public async getCarePlan(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.CarePlan', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    public async saveCarePlan(attachment: CM.CaseAssessmentAttachment): Promise<any> {
        const action = this.api.action('Iccc.CarePlanSave', `CM/Cases/Assessments/Attachments`, attachment);
        return await this.api.post(action);
    }

    public async loadCaseIcccGoals(caseId: string, includes: string[] = [], options?: Partial<IQueryLoadOptions>): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/cases/${caseId}/careplan/goals`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .includeWithState(options)
            .filterString('externalSystem.code', 'ICCC');

        includes.forEach(i => {
            if(i === 'problem' || i === 'interventions' || i === 'externalSystem') {
                return;
            }
            query.include(i);
        });

        return await this.api.get(query);
    }

    ////****************** Actions **************************************************////

    //Creates Section, Question, and Choice lookup records for use in reporting and correspondence
    public async createObjects(attachment: CM.CaseAssessmentAttachment): Promise<any> {
        const action = this.api.action('Iccc.CreateObjects', 'CM/Cases/Assessments/Attachments', attachment);
        return await this.api.post(action);
    }

    //Clears the ICCC viewer that is loaded in the IFrame
    public async clearViewer(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.AssessmentClear', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    //Loads the ICCC viewer that is loaded in the IFrame
    public async loadViewer(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.AssessmentLoad', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    //Clears the ICCC viewer that is loaded in the IFrame
    public async clearCarePlanViewer(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.CarePlanClear', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

    //Loads the ICCC viewer that is loaded in the IFrame
    public async loadCarePlanViewer(attachmentId: string): Promise<any> {
        const action = this.api.action('Iccc.CarePlanLoad', `CM/Cases/Assessments/Attachments/${attachmentId}`, {});
        return await this.api.post(action);
    }

}
